/**
 * PHARMAKON — Fournisseur ERP · notification store (Module 00)
 *
 * Per-staff supplier notification feed + unread badge + drawer state.
 * Replaces the prop-drilled badge/toast from the monolith (Appendix E.9).
 * Polls the unread count; fetches the full list when the drawer opens.
 *
 * Envelope: GET /fournisseur-portail/notifications →
 *   { success, data:[...], meta:{ total, unread, page, limit } }
 * (the api.client interceptor strips the transport layer; `body` is the above).
 */
import { create } from "zustand";
import notificationService from "../../api/fournisseur/notification.service.js";

const errOf = (e) =>
  e?.message ?? e?.response?.data?.message ?? "Erreur de chargement des notifications.";

const POLL_MS = 60_000;

export const useFournisseurNotifStore = create((set, get) => ({
  items: [],
  total: 0,
  unread: 0,
  page: 1,
  loading: false,
  error: null,
  drawerOpen: false,
  pollerRef: null,

  openDrawer: () => {
    set({ drawerOpen: true });
    get().fetchList({ page: 1, unread_only: false });
  },
  closeDrawer: () => set({ drawerOpen: false }),
  toggleDrawer: () => (get().drawerOpen ? get().closeDrawer() : get().openDrawer()),

  /** Lightweight unread count — used by the bell badge + poller. */
  fetchCount: async () => {
    try {
      const body = await notificationService.count();
      const unread =
        typeof body?.data?.unread === "number"
          ? body.data.unread
          : typeof body?.data?.count === "number"
            ? body.data.count
            : typeof body?.unread === "number"
              ? body.unread
              : 0;
      set({ unread });
    } catch {
      /* polling failures must never break the bell */
    }
  },

  /** Full list with the per-staff feed. opts: { page, unread_only }. */
  fetchList: async ({ page = 1, unread_only = false } = {}) => {
    set({ loading: true, error: null });
    try {
      const body = await notificationService.list({ page, limit: 20, unread_only });
      const items = Array.isArray(body?.data) ? body.data : [];
      const total = Number(body?.meta?.total ?? body?.total ?? items.length);
      const unread = Number(body?.meta?.unread ?? get().unread);
      set({ items, total, unread, page, loading: false });
      return items;
    } catch (e) {
      set({ error: errOf(e), loading: false });
    }
  },

  markRead: async (id) => {
    // optimistic
    set((s) => ({
      items: s.items.map((n) => (n.id === id ? { ...n, lue: true } : n)),
      unread: Math.max(0, s.unread - 1),
    }));
    try {
      await notificationService.markRead(id);
    } catch (e) {
      set({ error: errOf(e) });
      get().fetchCount();
    }
  },

  markAllRead: async () => {
    set((s) => ({ items: s.items.map((n) => ({ ...n, lue: true })), unread: 0 }));
    try {
      await notificationService.markAllRead();
    } catch (e) {
      set({ error: errOf(e) });
      get().fetchCount();
    }
  },

  startPoll: () => {
    if (get().pollerRef) return;
    get().fetchCount();
    const ref = setInterval(() => get().fetchCount(), POLL_MS);
    set({ pollerRef: ref });
  },

  stopPoll: () => {
    const ref = get().pollerRef;
    if (ref) clearInterval(ref);
    set({ pollerRef: null });
  },
}));

export default useFournisseurNotifStore;
